import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';

/**
 * One headline figure on the Stats Overview: the number, what it counts, where it came from,
 * and "Show the posts" to open the Posts tab filtered to it.
 */
export const StatTile: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  source?: string | null;
  /**
   * Filter the per-post list below to this metric.
   *
   * Clicking the number is the obvious way to ask "which post is that?", and the answer was
   * already on the page — just unreachable from the place the question gets asked. Only
   * offered when the figure is non-zero: there is nothing to show behind a zero.
   */
  onDrill?: () => void;
}> = ({ label, value, icon, color, source, onDrill }) => (
  <Card
    onClick={value > 0 ? onDrill : undefined}
    sx={{
      cursor: value > 0 && onDrill ? 'pointer' : 'default',
      transition: 'box-shadow 0.15s',
      '&:hover': value > 0 && onDrill ? { boxShadow: 3 } : undefined,
    }}
  >
    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 1.5 } }}>
        <Box sx={{ bgcolor: `${color}20`, color, borderRadius: 2, p: { xs: 0.75, sm: 1 }, display: 'flex', mr: { xs: 1, sm: 2 } }}>
          {icon}
        </Box>
        {/* Smaller on a phone, where two tiles share a row */}
        <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.35rem', sm: undefined } }}>
          {value.toLocaleString()}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {value > 0 && onDrill && (
        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
          Show the posts
        </Typography>
      )}
      {source && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {source}
        </Typography>
      )}
    </CardContent>
  </Card>
);
